import type { ApiResponse } from "@/types";

import { BaseApi } from "../base.api";

export type Granularity = "day" | "week" | "month";
export type TimelineMetric = "enrollments" | "users" | "courses";

export interface AnalyticsRange {
  from: string;
  to: string;
}

export interface AnalyticsQuery {
  from?: string;
  to?: string;
}

export interface ProviderRevenue {
  provider: "PAYME" | "CLICK" | "MANUAL" | "MOCK";
  amountUzs: number;
  count: number;
}

/**
 * Oynadagi pul.
 *
 * Uch xil son ATAYLAB: olingan, qaytarilgan va ayirma. Faqat "jami"
 * ko'rsatish qaytarishlarni yashirardi va hisobot bank ko'chirmasi
 * bilan mos kelmasdi.
 */
export interface RevenueSummary {
  paidUzs: number;
  refundedUzs: number;
  /** Sotuv aylanmasi: olingan − qaytarilgan. */
  netUzs: number;
  /** Platforma sof daromadi: har to'lovning o'z stavkasi bilan, qaytarilganlari ayirilgan. */
  platformFeeUzs: number;
  /** O'qituvchilarga: `netUzs − platformFeeUzs`. */
  teacherShareUzs: number;
  paymentCount: number;
  refundCount: number;
  byProvider: ProviderRevenue[];
}

export interface EnrollmentSummary {
  /** Oynada YARATILGAN yozilishlar. */
  newInRange: number;
  /** Qolganlari — HOZIRGI holat, oynaga bog'liq emas. */
  active: number;
  completed: number;
  pending: number;
  cancelled: number;
}

export interface UserSummary {
  total: number;
  newInRange: number;
  blocked: number;
  byRole: { role: "admin" | "moderator" | "teacher" | "student"; count: number }[];
}

export interface CourseSummary {
  total: number;
  published: number;
  pendingReview: number;
  draft: number;
  live: number;
  recorded: number;
}

export interface TopCourse {
  courseId: number;
  title: string;
  /** Sof tushum (aylanma). */
  revenueUzs: number;
  /** Shu kursdan platforma ulushi. */
  platformFeeUzs: number;
  paymentCount: number;
}

export interface AnalyticsOverview {
  range: AnalyticsRange;
  revenue: RevenueSummary;
  enrollments: EnrollmentSummary;
  users: UserSummary;
  courses: CourseSummary;
  topCourses: TopCourse[];
}

export interface RevenuePointDto {
  /** Bo'lak boshi, `YYYY-MM-DD`. */
  bucket: string;
  paidUzs: number;
  /** Shu bo'lakdagi to'lovlardan platforma ulushi. */
  platformFeeUzs: number;
  count: number;
}

export interface RevenueSeries {
  range: AnalyticsRange;
  granularity: Granularity;
  points: RevenuePointDto[];
}

export interface TimelinePoint {
  bucket: string;
  count: number;
}

export interface TimelineSeries {
  range: AnalyticsRange;
  metric: TimelineMetric;
  granularity: Granularity;
  points: TimelinePoint[];
}

/**
 * Analitika API si — backend'dagi `AnalyticsController` ning ko'zgusi.
 *
 * Ilgari bu ekran daromadni oxirgi 500 ta to'lovni yuklab, BRAUZERDA
 * qo'shib hisoblardi va raqam yonida "namuna" deb yozib turardi. Endi
 * yig'ma serverda: son to'liq va bitta so'rov.
 */
export class AnalyticsApi extends BaseApi {
  private readonly baseUrl: string = "/analytics";

  overview(query: AnalyticsQuery = {}) {
    return this.request.get<AnalyticsOverview, ApiResponse<AnalyticsOverview>>(
      `${this.baseUrl}/overview`,
      { params: query },
    );
  }

  revenue(query: AnalyticsQuery & { granularity?: Granularity } = {}) {
    return this.request.get<RevenueSeries, ApiResponse<RevenueSeries>>(`${this.baseUrl}/revenue`, {
      params: query,
    });
  }

  timeline(query: AnalyticsQuery & { metric?: TimelineMetric; granularity?: Granularity } = {}) {
    return this.request.get<TimelineSeries, ApiResponse<TimelineSeries>>(
      `${this.baseUrl}/timeline`,
      { params: query },
    );
  }
}

export const analyticsApi = new AnalyticsApi();
